import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import rollbar from '../utils/rollbar';

import Grid from '@material-ui/core/Grid';
import MaterialTable from 'material-table';

import tableIcons from '../constants/tableIcons';
import { prepareMaterialTableData } from '../utils/helpers';
import { envApiUriMapping } from '../constants/api-uris';
import getEnv from '../utils/get-env';
import { useHybridAuthToken } from '../components/hybridAuthToken';
import { DateTime } from 'luxon';
import { gql, useQuery } from '@apollo/client';
import Tooltip from '@material-ui/core/Tooltip';
import axios from 'axios';
import Box from '@material-ui/core/Box';
import { useTheme } from '@material-ui/core/styles';

const HOSTNAME = window.location.hostname;
const baseUrl = envApiUriMapping[getEnv(HOSTNAME)].experienceNotificationsUri;

const notificationDeliveryStatus = {
  scheduled: { label: 'Scheduled', tooltip: 'Notification is scheduled to be sent.' },
  pending: { label: 'Pending', tooltip: 'Notification is about to be sent.' },
  sent_unconfirmed: {
    label: 'Sent (Unconfirmed)',
    tooltip: 'For push notifications after they have been accepted for delivery, but not confirmed.',
  },
  sent: { label: 'Sent', tooltip: 'Notification has been delivered successfully.' },
  failed: { label: 'Failed', tooltip: 'Notificaiton failed to be delivered.' },
};

const notificationDeliveryType = {
  push: 'Push',
  sms: 'SMS',
  email: 'Email',
};

const SITE_DETAILS_CONTACTS_QUERY = gql`
  query SiteDetailsContactsQuery($prospectId: String!) {
    prospect(prospectId: $prospectId) {
      id
      contacts {
        id
        contactId
        name
        phone
        email
        relation
        isPrimary
        roles
      }
    }
  }
`;

export const getNotificationDevices = async (prospectId, contactIds, hybridAuth) => {
  try {
    const response = await axios.post(
      `${baseUrl}notification-device/search`,
      { searchCriteria: { contactIds, prospectIds: [prospectId] } },
      { headers: { Authorization: hybridAuth } },
    );
    return response.data;
  } catch (error) {
    rollbar.error(error);
  }
};

export const getNotifications = async (prospectId, contactIds, hybridAuth) => {
  try {
    const response = await axios.post(
      `${baseUrl}notification/search`,
      { searchCriteria: { contactIds, includeDeliveryRecords: true, prospectIds: [prospectId] } },
      { headers: { Authorization: hybridAuth } },
    );
    return response.data;
  } catch (error) {
    rollbar.error(error);
  }
};

const DevicesTable = ({ data: dt, loading }) => {
  const data = prepareMaterialTableData(dt);
  return (
    <MaterialTable
      title="Devices"
      columns={[
        { title: 'ID', field: 'id' },
        { title: 'Login ID', field: 'login_id' },
        { title: 'Enabled', field: 'enabled', render: (rowData) => (rowData.enabled ? 'Yes' : 'No') },
        {
          title: 'Created On',
          field: 'created_on',
          render: (rowData) => DateTime.fromISO(rowData.created_on).toLocaleString(DateTime.DATETIME_FULL),
        },
        {
          title: 'Updated On',
          field: 'updated_on',
          render: (rowData) => DateTime.fromISO(rowData.updated_on).toLocaleString(DateTime.DATETIME_FULL),
        },
      ]}
      data={data}
      icons={tableIcons}
      isLoading={loading}
      options={{
        draggable: false,
        paging: data.length > 5,
        search: true,
        showEmptyDataSourceMessage: !loading,
      }}
    />
  );
};

const DeliveryRecordsTable = ({ data: dt }) => {
  const data = prepareMaterialTableData(dt);
  return (
    <MaterialTable
      columns={[
        {
          title: 'Delivery Type',
          field: 'delivery_type',
          render: (rowData) => notificationDeliveryType[rowData.delivery_type],
        },
        {
          title: 'Status',
          field: 'status',
          render: (rowData) => (
            <Tooltip title={notificationDeliveryStatus[rowData.status].tooltip} placement="top-start">
              <div>{notificationDeliveryStatus[rowData.status].label}</div>
            </Tooltip>
          ),
        },
        { title: 'Recipient', field: 'recipient' },
        {
          title: 'Sent On',
          field: 'sent_on',
          render: (rowData) => DateTime.fromISO(rowData.sent_on).toLocaleString(DateTime.DATETIME_FULL),
        },
        { title: 'Error', field: 'error' },
      ]}
      data={data}
      icons={tableIcons}
      options={{
        draggable: false,
        paging: false,
        search: false,
        showEmptyDataSourceMessage: true,
        showTitle: false,
        toolbar: false,
      }}
      style={{ boxShadow: 'unset' }}
    />
  );
};

const NotificationsTable = ({ data: dt, loading }) => {
  const theme = useTheme();
  const data = prepareMaterialTableData(dt);
  return (
    <MaterialTable
      title="Notifications"
      columns={[
        { title: 'Type', field: 'notification_group' },
        { title: 'Title', field: 'title' },
        { title: 'Body', field: 'body' },
        {
          title: 'Date Sent',
          field: 'send_date',
          render: (rowData) => DateTime.fromISO(rowData.send_date).toLocaleString(DateTime.DATETIME_FULL),
        },
        {
          title: 'Visible in app',
          field: 'display_in_app',
          render: (rowData) => (rowData.display_in_app ? 'Yes' : 'No'),
        },
      ]}
      data={data}
      detailPanel={(rowData) => (
        <Box
          sx={{
            border: `1px solid ${theme.palette.grey['300']}`,
            borderRadius: '10px',
            margin: '30px',
            overflow: 'hidden',
          }}
        >
          <DeliveryRecordsTable data={rowData.delivery_records} />
        </Box>
      )}
      icons={tableIcons}
      isLoading={loading}
      onRowClick={(_event, _rowData, toggleDetailPanel) => toggleDetailPanel()}
      options={{
        draggable: false,
        paging: data.length > 5,
        search: true,
        showEmptyDataSourceMessage: !loading,
      }}
    />
  );
};

const SiteDetailsContacts = () => {
  const hybridAuth = useHybridAuthToken();
  const { contactId, prospectId } = useParams();

  const [notificationData, setNotificationData] = useState([]);
  const [deviceData, setDeviceData] = useState([]);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);

  const { data: contactData } = useQuery(SITE_DETAILS_CONTACTS_QUERY, {
    returnPartialData: true,
    variables: { prospectId },
  });

  const contactIds = useMemo(
    () => (contactData?.prospect?.contacts || []).map((contact) => contact.contactId),
    [contactData?.prospect?.contacts],
  );

  useEffect(() => {
    const fetchNotifications = async () => {
      setIsLoadingNotifications(true);
      const data = await getNotifications(prospectId, contactIds, hybridAuth);
      if (data?.notifications) {
        setNotificationData(data.notifications);
      }
      setIsLoadingNotifications(false);
    };

    if ((prospectId || contactIds.length > 0) && hybridAuth) {
      fetchNotifications();
    }
  }, [contactIds, hybridAuth, prospectId]);

  useEffect(() => {
    const fetchDevices = async () => {
      setIsLoadingDevices(true);
      const data = await getNotificationDevices(prospectId, [contactId], hybridAuth);
      if (data?.notificationDevices) {
        setDeviceData(data.notificationDevices);
      }
      setIsLoadingDevices(false);
    };

    if (contactId && hybridAuth) {
      fetchDevices();
    }
  }, [contactId, hybridAuth, prospectId]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <DevicesTable data={deviceData} loading={isLoadingDevices} />
      </Grid>
      <Grid item xs={12}>
        <NotificationsTable data={notificationData} loading={isLoadingNotifications} />
      </Grid>
    </Grid>
  );
};

export default SiteDetailsContacts;

const deliveryRecordsShape = PropTypes.arrayOf(
  PropTypes.shape({
    id: PropTypes.string,
    delivery_type: PropTypes.oneOf(['push', 'sms', 'email']),
    notification_id: PropTypes.string,
    push_receipt: PropTypes.string,
    recipient: PropTypes.string,
    sent_on: PropTypes.string,
    status: PropTypes.string,
    error: PropTypes.string,
  }),
);

const notificationShape = PropTypes.shape({
  body: PropTypes.string,
  created_on: PropTypes.string,
  deleted: PropTypes.bool,
  delivery_records: deliveryRecordsShape,
  display_in_app: PropTypes.bool,
  id: PropTypes.string,
  meta_data: PropTypes.object,
  notification_group: PropTypes.string,
  notification_type: PropTypes.string,
  send_date: PropTypes.string,
  target_id: PropTypes.string,
  target_type: PropTypes.string,
  title: PropTypes.string,
  updated_on: PropTypes.string,
  viewed_on: PropTypes.string,
});

DeliveryRecordsTable.propTypes = {
  data: deliveryRecordsShape.isRequired,
};

DevicesTable.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      contact_ids: PropTypes.arrayOf(PropTypes.string),
      created_on: PropTypes.string,
      enabled: PropTypes.bool,
      id: PropTypes.string,
      login_id: PropTypes.string,
      prospect_ids: PropTypes.arrayOf(PropTypes.string),
      updated_on: PropTypes.string,
    }),
  ).isRequired,
  loading: PropTypes.bool.isRequired,
};

NotificationsTable.propTypes = {
  data: PropTypes.arrayOf(notificationShape).isRequired,
  loading: PropTypes.bool.isRequired,
};
